import MvAddressSelectorSDKOptions, {
    MvOptionsErrors,
    MvTieredPrice,
} from "models/MvAddressSelectorSDKOptions";

const isNonNegativeInteger = (value: unknown): value is number =>
    typeof value === "number" && Number.isInteger(value) && value >= 0;

const isValidPrice = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value) && value >= 0;

const isValidTier = (tier: unknown): tier is MvTieredPrice => {
    if (typeof tier !== "object" || tier === null) {
        return false;
    }

    const { min, pricePerAddress } = tier as MvTieredPrice;
    return isNonNegativeInteger(min) && isValidPrice(pricePerAddress);
};

export const isZipCodeSelectionMode = (
    options: MvAddressSelectorSDKOptions,
): boolean => options.zipCodeAreaMinCount !== undefined;

export const validateOptions = (
    options: MvAddressSelectorSDKOptions,
): MvOptionsErrors => {
    const {
        addressMinimum,
        addressLimit,
        tieredPrices,
        pricePerAddress,
        zipCodeAreaMinCount,
    } = options;

    const errors: string[] = [];

    if (zipCodeAreaMinCount !== undefined) {
        if (!isNonNegativeInteger(zipCodeAreaMinCount)) {
            errors.push("zipCodeAreaMinCount must be a non-negative integer.");
        }
    }

    if (
        (addressMinimum !== undefined &&
            !isNonNegativeInteger(addressMinimum)) ||
        (addressLimit !== undefined && !isNonNegativeInteger(addressLimit))
    ) {
        errors.push("Address limits must be non-negative integers.");
    }

    if (
        addressMinimum !== undefined &&
        addressLimit !== undefined &&
        addressMinimum > addressLimit
    ) {
        errors.push("minAddressCount cannot be greater than maxAddressCount.");
    }

    if (pricePerAddress !== undefined && !isValidPrice(pricePerAddress)) {
        errors.push("pricePerAddress must be a non-negative finite number.");
    }

    if (tieredPrices !== undefined && pricePerAddress !== undefined) {
        errors.push("pricePerAddress & tieredPrices can not be used together");
    }

    if (tieredPrices === undefined && pricePerAddress === undefined) {
        errors.push("use either pricePerAddress or tieredPrices ");
    }

    if (tieredPrices !== undefined) {
        if (!Array.isArray(tieredPrices) || tieredPrices.length === 0) {
            errors.push("tieredPrices must contain at least one tier.");
        } else {
            let previousMin = -1;
            for (let index = 0; index < tieredPrices.length; index += 1) {
                const tier = tieredPrices[index];
                if (!isValidTier(tier)) {
                    errors.push(
                        "Each price tier must have a non-negative integer min and a non-negative finite pricePerAddress.",
                    );
                    continue;
                }
                if (index === 0 && tier.min !== 0) {
                    errors.push(
                        "The first tieredPrices entry must have min: 0.",
                    );
                }
                if (tier.min <= previousMin) {
                    errors.push(
                        "tieredPrices must be strictly ordered by min.",
                    );
                }
                previousMin = tier.min;
            }
        }
    }
    if (errors.length > 0) {
        return errors;
    }

    return null;
};
